<template>
  <div v-if="true" class="verify-card">
    <img :src="isVerified ? circleTick : emailIcon" :alt="isVerified ? 'Success Icon' : 'Email Icon'"
      class="email-icon" />
  
    <h2>{{ isVerified ? "Login successful" : "Check your Email" }}</h2>
    <p v-if="isVerified" class="subtext">
      Please click the button below to continue to the interface
    </p>
    <p v-else class="subtext">
      We sent verification code to
      <span class="email">{{ props.email }}</span>
    </p>
  
    <div v-if="isReadyToVerify && !isVerified" class="code-inputs">
      <input v-for="(val, idx) in codeInputs" :key="idx" v-model="codeInputs[idx]" :data-idx="idx" maxlength="1"
        inputmode="numeric" pattern="\d*" @input="handleInput" type="text" />
    </div>
  
    <button :disabled="isLoading || (isReadyToVerify && fullCode.length < 4)" class="verify-btn" @click="verifyMail">
      <span v-if="isLoading">
        <i class="pi pi-spinner pi-spin"></i> Loading...
      </span>
      <span v-else>{{
        isVerified
        ? "Continue"
        : isReadyToVerify
        ? "Verify OTP"
        : "Enter code manually"
        }}</span>
    </button>
  
    <p v-if="!isVerified" class="resend-text">
      Didn’t receive the email?
      <a @click="resendMail" :class="{ disabled: isDisabled }" :title=" isDisabled
                      ? 'Please wait 30 seconds before resending' : ''">Click to resend</a>
    </p>
  
    <div v-if="!isVerified" class="back-login" @click="goBack">
      <span class="arrow">&#8592;</span>
      <span>Back to Login</span>
    </div>
  
  
    <!-- Toast inside -->
    <div class="toast" v-if="showToast">
      <div class="profile-update errorUpdate">
        <img class="bold-essentional-ui-error" alt="error icon" src="../assets/close-circle.png" />
        <div class="profile-updated-successfully">
          Oops! The code you entered is incorrect or expired. Please check your
          email and try again.
        </div>
        <button class="image-delete-icon" @click="reset">
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M1 9.75L10 1.25M1 1.25L10 9.75" stroke="#7C7C7C" stroke-width="1.2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup >
import { ref, computed, nextTick, defineProps } from "vue";
import emailIcon from "@/assets/icons/emailIcon.png";
import circleTick from "@/assets/icons/CircleTick.png";
import axios from "axios";
import { useRouter } from "vue-router";
import { saveImageToDB } from '@/store/indexeddb.ts'

const router = useRouter();
const props = defineProps({
  email: {
    type: String,
    required: true,
  },
  remember: {
    type: Boolean,
    required: true,
  },
});
const isReadyToVerify = ref(false);
const isVerified = ref(false);
const showToast = ref(false);
const isDisabled = ref(false);
const codeInputs = ref(["", "", "", ""]);
const isLoading = ref(false);
const fullCode = computed(() => codeInputs.value.join(""));
const userData = ref();
const handleInput = async (e) => {
  const input = e.target;
  const idx = parseInt(input.dataset.idx);
  const numeric = input.value.replace(/\D/g, ""); // Allow only digits

  codeInputs.value[idx] = numeric;

  // Move to next input only if a digit was entered and not at last index
  if (numeric && idx < codeInputs.value.length - 1) {
    nextTick(() => {
      const nextInput = document.querySelector(`input[data-idx="${idx + 1}"]`);
      if (nextInput && typeof nextInput.focus === "function") {
        nextInput.focus();
      }
    });
  }
};

const verifyMail = async () => {
  if (isVerified.value) {
    isLoading.value = true;
    sessionStorage.removeItem("logoutState");
    window.onpopstate = null;

    await saveImageToDB('user-image', userData.value.image);
    await router.push(userData.value.redirectUrl);

    if (!userData.value.isSettingsUpdated) {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("update-settings", {
            detail: {
              success: true,
              message:
                "Complete your settings page to access all features and benefits",
            },
          })
        );
      }, 100);
    }
    isLoading.value = false;
    return;
  }
  if (isReadyToVerify.value) {
    try {
      isLoading.value = true;
      const response = await axios.post(
        "/api/manage/login/verify",
        {
          Email: props.email,
          VerificationCode: fullCode.value,
          RememberMe: props.remember,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = response.data;

      if (response.status === 200 && data.success) {
        userData.value = response.data;
        isVerified.value = true;
        isLoading.value = false;
        return;
      }
      showToast.value = true;
      setTimeout(() => {
        showToast.value = false;
        isLoading.value = false;
      }, 3000);
      return;
    } catch {
      showToast.value = true;
      setTimeout(() => {
        showToast.value = false;
        isLoading.value = false;
      }, 3000);
    }
  }
  isReadyToVerify.value = true;
};

const goBack = async () => {
  try {
    await axios.post(
      "/api/manage/login/clear-otp"
    );
  } catch {
    console.log();
  }
  window.location.reload();
};

const resendMail = async () => {
  if (isDisabled.value) {
    return
  }
  try {
    isDisabled.value = true;
    await axios.post(
      "/api/manage/login/verify/resend",
      {
        Email: props.email,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    setTimeout(() => {
      isDisabled.value = false
    }, 30000)
  } catch {
    isDisabled.value = false;
  }
};

const reset = () => {
  isLoading.value = false;
  showToast.value = false;
};
</script>

<style scoped>
.verify-card {
  width: 100%;
  max-width: 600px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: relative;
  flex-shrink: 0;
  padding: 40px;
  box-sizing: border-box;
  border: 1px solid var(--Border-Primary-100, #efefef);
  border-radius: 10px;
  background-color: white;
}

.email-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 10px;
}

.bold-essentional-ui-error {
  width: 21px;
  height: 21px;
}

.toast {
  position: absolute;
  right: 50%;
  top: -7%;
  width: 75%;
  translate: 50%;

}

.image-delete-icon {
  border: 0;
  background: none;
  cursor: pointer;
  padding-top: 2px;
}

.profile-updated-successfully {
  position: relative;
  line-height: 150%;
  width: 87%;
  font-size: 12px;
  color: #1c1c1c;
  flex-grow: 1;
}

.subtext {
  color: #666;
  margin-bottom: 30px;
}

.email {
  font-weight: 600;
  color: #000;
}

.code-inputs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 25px;
}

.code-inputs input {
  width: 50px;
  height: 50px;
  font-size: 20px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s ease;
}

.code-inputs input:focus {
  border-color: #007bff;
}

.verify-btn {
  background-color: #007bff;
  color: white;
  padding: 12px;
  width: 50%;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 20px;
}

.verify-btn:disabled {
  background-color: #91d8f7;
  cursor: not-allowed;
}

.verify-btn:hover {
  background-color: #0056b3;
}

.resend-text {
  font-size: 14px;
  color: #666;
}

.resend-text a {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.resend-text a:hover {
  text-decoration: underline;
  cursor: pointer;
}

.resend-text a.disabled {
  color: gray;
  cursor: not-allowed;
}

.resend-text a.disabled:hover {
  color: gray;
  /* Override hover color */
}

.back-login {
  margin-top: 20px;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.back-login:hover {
  cursor: pointer;
  color: black;
}

.arrow {
  font-size: 16px;
}

.profile-update.errorUpdate {
  background-color: #ffeaea;
}

.profile-update {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  padding: 10px;
  border-radius: 4px;
  position: relative;
}
</style>